import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface ResendFormData {
  email: string;
}

interface ManualVerificationFormData {
  token: string;
}

type VerificationStatus = 'success' | 'error' | 'resent' | null;

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail, resendVerification, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>(null);
  // const [email, setEmail] = useState('');

  const {
    register: registerResend,
    handleSubmit: handleResendSubmit,
    formState: { errors: resendErrors }
  } = useForm<ResendFormData>();

  const {
    register: registerManual,
    handleSubmit: handleManualSubmit,
    formState: { errors: manualErrors }
  } = useForm<ManualVerificationFormData>();

  useEffect(() => {
    const token = searchParams.get('token');
    console.log(token);
    if (token) {
      handleVerifyToken(token);
    }
  }, [searchParams]);

  const handleVerifyToken = async (token: string) => {
    setIsLoading(true);
    try {
      const result = await verifyEmail(token);
      if (result.success) {
        setVerificationStatus('success');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setVerificationStatus('error');
      }
    } catch (error) {
      setVerificationStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async (data: ResendFormData) => {
    setIsLoading(true);
    try {
      const result = await resendVerification(data.email);
      if (result.success) {
        setVerificationStatus('resent');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualVerification = async (data: ManualVerificationFormData) => {
    setIsLoading(true);
    try {
      const result = await verifyEmail(data.token);
      if (result.success) {
        setVerificationStatus('success');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setVerificationStatus('error');
      }
    } catch (error) {
      setVerificationStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-white mb-2">Email Verified!</h1>
          <p className="text-gray-400 mb-4">Your email has been successfully verified. Redirecting to the app...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="flex items-center p-4">
        <button
          onClick={() => {logout(); navigate('/login');}}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Login
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className="w-full max-w-md">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-3xl font-serif text-white mb-2">Email Verification Required</h1>
            <p className="text-gray-400">Please verify your email address to access the application</p>
          </div>

          {/* Alert Message */}
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-yellow-400 font-medium mb-1">Access Restricted</h3>
                <p className="text-gray-300 text-sm">
                  Your account is not yet verified. You must verify your email address before you can access any part of the application.
                </p>
              </div>
            </div>
          </div>

          {/* Manual Token Verification */}
          <div className="card mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Manual Verification</h3>
            <form onSubmit={handleManualSubmit(handleManualVerification)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Verification Token
                </label>
                <input
                  type="text"
                  {...registerManual('token', { required: 'Token is required' })}
                  className="input-field w-full"
                  placeholder="Enter verification token"
                />
                {manualErrors.token && (
                  <p className="text-red-400 text-sm mt-1">{manualErrors.token.message}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full"
              >
                {isLoading ? 'Verifying...' : 'Verify Email'}
              </button>
            </form>
          </div>

          {/* Resend Verification */}
          <div className="card">
            <h3 className="text-lg font-semibold text-white mb-4">Resend Verification Email</h3>
            <form onSubmit={handleResendSubmit(handleResendVerification)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  {...registerResend('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className="input-field w-full"
                  placeholder="john@example.com"
                />
                {resendErrors.email && (
                  <p className="text-red-400 text-sm mt-1">{resendErrors.email.message}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-secondary w-full"
              >
                {isLoading ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </form>
          </div>

          {/* Instructions */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Check your email for a verification link. If you don't see it, check your spam folder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 